"use client";

import OutlineTextButton from "@/components/CircularButton/styled/OutlineTextButton";
import ConditionalTooltip from "@/components/ConditionalTooltip";
import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import Tags, { getTagHref } from "@/components/Tags";
import ClickableText from "@/components/styled/button/ClickableText";
import Mountable from "@/components/styled/div/Mountable";
import LinkWrapper from "@/components/wrappers/Link/Link";
import { SetEditInput } from "@/features/FlashcardsEditingPage/components/styled/SetEditInputs";
import { useSetEditingContextSelector } from "@/features/FlashcardsEditingPage/hooks/useSetEditingContext";
import { useTaggingContextSelector } from "@/features/Tagging/hooks/useTaggingContext";
import { borderRadius } from "@/utils/borderRadius";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { FlashcardSet } from "@knowt/syncing/graphql/schema";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { KNOWT_GAMIFICATION_STORAGE } from "@knowt/syncing/hooks/gamification/constants";
import { TagValueWithMetadata, useCombinedTags } from "@knowt/syncing/hooks/tags/useTags";
import { renameKey } from "@knowt/syncing/utils/genericUtils";
import { noop } from "lodash-es";
import { Plus } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const FlashcardSetDescriptionPopup = dynamic(() => import("@/components/Popup/FlashcardSetDescriptionPopup"), {
    ssr: false,
});

export const TagList = ({
    flashcardSet,
    readOnly,
    tags,
}: {
    flashcardSet: FlashcardSet;
    readOnly: boolean;
    tags: TagValueWithMetadata[];
}) => {
    const { updateFlashcardSetMetaData } = useFlashcardSet({ flashcardSetId: flashcardSet?.flashcardSetId });

    const openTaggingPopup = useTaggingContextSelector(state => state.openTaggingPopup);
    const closeTaggingPopup = useTaggingContextSelector(state => state.closeTaggingPopup);

    const onAddTagClick = () => {
        openTaggingPopup({
            flashcardSet,
            onSave: async newTags => {
                renameKey(newTags, "exam", "exam_v2");
                renameKey(newTags, "custom", "tags");
                await updateFlashcardSetMetaData(newTags);

                closeTaggingPopup();
            },
        });
    };

    return (
        <Tags>
            {({ renderTag }) => (
                <FlexRowAlignCenter style={{ gap: "1.4rem 0.6rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                    <Mountable mount={!!tags.length}>
                        {tags.map((tag, i: number) => {
                            const href = getTagHref(tag);
                            return (
                                <LinkWrapper
                                    key={tag.value + i}
                                    href={href}
                                    rel={href.includes("/search") ? "nofollow" : undefined}>
                                    {renderTag({
                                        value: tag.value,
                                        sx: {
                                            paddingRight: "1.5rem",
                                            fontSize: "1.4rem",
                                            backgroundColor: themeColors.background,
                                        },
                                        key: tag.value + i,
                                    })}
                                </LinkWrapper>
                            );
                        })}
                    </Mountable>

                    {/* ADD TAG BTN */}
                    <OutlineTextButton
                        disabled={readOnly}
                        onClick={onAddTagClick}
                        className="secondaryText2"
                        sx={{ padding: `0.6rem 1.6rem`, gap: "1rem", visibility: readOnly ? "hidden" : "visible" }}>
                        <p>Add tags</p>
                        <Plus size={iconSizes.SM} strokeWidth={strokeWidth.normal} />
                    </OutlineTextButton>
                </FlexRowAlignCenter>
            )}
        </Tags>
    );
};

const NoTagsOrDescription = () => {
    return (
        <Flex
            style={{
                padding: "2rem",
                borderRadius: borderRadius.card,
                backgroundColor: themeColors.neutralWhite,
                flex: 2.5,
                gap: spacing.SM,
            }}>
            <Image
                src={`/images/tags-icon.svg`}
                alt="encourage image"
                width={50}
                height={50}
                style={{ objectFit: "contain", margin: "auto" }}
            />
            <FlexColumn style={{ width: "100%" }}>
                <h3 className="bodyBold1">There's no tags or description</h3>
                <p className="secondaryText1">Looks like no tags are added yet.</p>
            </FlexColumn>
        </Flex>
    );
};

type SetOverviewTagsDescriptionProps = {
    flashcardSet: FlashcardSet;
    readOnly?: boolean;
    fromOverViewPage?: boolean;
};

export const SetOverviewTagsDescription = ({
    flashcardSet: initialFlashcardSet,
    readOnly,
    fromOverViewPage = false,
}: SetOverviewTagsDescriptionProps) => {
    const { updateFlashcardSetMetaData, flashcardSet } = useFlashcardSet({
        flashcardSetId: initialFlashcardSet?.flashcardSetId,
    });

    const defintionInputRef = useRef<HTMLInputElement>(null);
    const { tagsValueWithMetadata } = useCombinedTags(flashcardSet || initialFlashcardSet);
    const pendingUpdates = useSetEditingContextSelector(s => s.pendingUpdates);

    const description = flashcardSet?.description || "";
    const [isDescPopupOpen, setIsDescPopupOpen] = useState(false);
    const [isClamp, setIsClamp] = useState(false);
    const [isMore, setIsMore] = useState(false);

    const descRef = useRef<HTMLParagraphElement>(null);
    useEffect(() => {
        if (!descRef.current) return;
        setIsClamp(descRef.current.scrollHeight > descRef.current.clientHeight);
    }, [description]);

    // EMPTY STATE
    if (readOnly && !tagsValueWithMetadata.length && !description) {
        return <NoTagsOrDescription />;
    }

    return (
        <>
            <FlexColumn
                style={{
                    flex: 2.5,
                    borderRadius: borderRadius.card,
                    backgroundColor: themeColors.neutralWhite,
                    position: "relative",
                }}>
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        borderRadius: "0 2rem 0 2rem",
                        backgroundColor: themeColors.neutralBlack,
                        color: themeColors.neutralWhite,
                        padding: "0.8rem 1.6rem",
                    }}>
                    <FlexRowAlignCenter style={{ gap: "0.5rem" }}>
                        <Image
                            src={`${KNOWT_GAMIFICATION_STORAGE}/xp-icon.svg`}
                            alt="flashcard set"
                            width={16}
                            height={16}
                        />
                        <h1 className="secondaryTextBold1" style={{ color: themeColors.neutralWhite }}>
                            Earn XP
                        </h1>
                    </FlexRowAlignCenter>
                </div>

                <FlexColumn style={{ padding: "1.6rem 2.4rem" }}>
                    <h2 className="bodyBold2" style={{ marginBottom: "1.2rem" }}>
                        Description and Tags
                    </h2>

                    <Mountable mount={!readOnly && !description && fromOverViewPage}>
                        <OutlineTextButton
                            onClick={() => setIsDescPopupOpen(true)}
                            className="secondaryText2"
                            sx={{
                                color: themeColors.neutral3,
                                padding: `0.8rem 1.6rem`,
                                gap: spacing.XS,
                                width: "fit-content",
                            }}>
                            <p>Add description</p>
                            <Plus size={iconSizes.SM} />
                        </OutlineTextButton>
                    </Mountable>

                    {!readOnly && !fromOverViewPage && (
                        <SetEditInput
                            ref={defintionInputRef}
                            defaultValue={flashcardSet?.description || ""}
                            onChange={e => {
                                pendingUpdates.current.metadata.description = e.target.value;
                            }}
                            placeholder={"Add a description"}
                            className="body2"
                            style={{
                                border: "none",
                                backgroundColor: themeColors.background,
                                marginBottom: spacing.XS,
                            }}
                        />
                    )}

                    {fromOverViewPage && (
                        <ConditionalTooltip placement="left-start" tooltip={!readOnly ? "Edit description" : null}>
                            <p
                                ref={descRef}
                                className="secondaryText1 clampText"
                                onClick={!readOnly ? () => setIsDescPopupOpen(true) : noop}
                                style={{
                                    wordBreak: "break-word",
                                    WebkitLineClamp: isMore ? 100 : 2,
                                    lineClamp: isMore ? 100 : 2,
                                    cursor: !readOnly ? "pointer" : "unset",
                                }}>
                                {description}
                            </p>
                        </ConditionalTooltip>
                    )}

                    <div style={{ paddingBottom: "1.2rem" }} />

                    <TagList flashcardSet={flashcardSet} tags={tagsValueWithMetadata || []} readOnly={!!readOnly} />

                    <Mountable mount={isClamp}>
                        <ClickableText
                            className="secondaryText1"
                            style={{ marginTop: spacing.XS }}
                            onClick={() => setIsMore(!isMore)}>
                            {isMore ? "Show less" : "Show more"}
                        </ClickableText>
                    </Mountable>
                </FlexColumn>
            </FlexColumn>

            <LazyLoaded load={isDescPopupOpen}>
                <FlashcardSetDescriptionPopup
                    isOpen={isDescPopupOpen}
                    onClose={() => setIsDescPopupOpen(false)}
                    defaultName={description}
                    onConfirm={async description => {
                        await updateFlashcardSetMetaData({ description });
                        toast.success("Flashcard set description added!");
                    }}
                />
            </LazyLoaded>
        </>
    );
};

export default SetOverviewTagsDescription;
